import Device from 'Faster/model/Device';
import DeviceData from 'Faster/model/DeviceData';
import CreateDeviceRequest from 'Faster/request/CreateDeviceRequest';
import UpdateDeviceRequest from 'Faster/request/UpdateDeviceRequest';
import DeviceDataJsonTransformer from 'Faster/serialization/DeviceDataJsonTransformer';
import SessionService from 'Faster/service/SessionService';
import DeviceStorage from 'Faster/storage/DeviceStorage';
import FasterConfiguration from 'Faster/FasterConfiguration';
import FasterHttpClient from 'Faster/network/FasterHttpClient';
import isPrivateMode from 'Faster/isPrivateMode';
import { UAParser } from 'ua-parser-js';
import Session from 'Faster/model/Session';

export default class DeviceService {
  private httpClient: FasterHttpClient;
  private deviceStorage: DeviceStorage;
  private sessionService: SessionService;
  private configuration: FasterConfiguration;

  private deviceIsExternal = false;

  public constructor(
    deviceStorage: DeviceStorage,
    sessionService: SessionService,
    configuration: FasterConfiguration,
    httpClient: FasterHttpClient,
    externalDeviceId?: string
  ) {
    this.sessionService = sessionService;
    this.deviceStorage = deviceStorage;
    this.configuration = configuration;
    this.httpClient = httpClient;

    this.deviceIsExternal = typeof externalDeviceId === 'string';
  }

  public init(trackInstall: boolean): Promise<void> {
    if (this.deviceIsExternal) {
      return;
    }

    if (this.hasDeviceData()) {
      this.refresh();

      return;
    }
    return this.createDevice(trackInstall);
  }

  public get(): Device | undefined {
    return this.deviceStorage.getDevice();
  }

  public updateDeviceData(device: Device): void {
    if (this.deviceIsExternal) {
      console.warn('External device ID provided, device data manipulation is disabled.');

      return;
    }

    const request = new UpdateDeviceRequest(device, new Date());

    this.httpClient.execute(request).catch(() => {});
    this.deviceStorage.data = DeviceData.fromDevice(device);
  }

  private refresh(): void {
    const savedData = this.deviceStorage.data;
    const currentData = this.collectDeviceData();

    if (!this.deviceStorage.isRemotelyPersisted) {
      const device = new Device(this.deviceStorage.deviceId, this.deviceStorage.data);
      this.persistDevice(device, this.sessionService.get(), false);
    }

    const currentJson = DeviceDataJsonTransformer.toJSON(currentData);
    const savedJson = DeviceDataJsonTransformer.toJSON(savedData);
    if (JSON.stringify(currentJson) != JSON.stringify(savedJson)) {
      const deviceId = this.deviceStorage.deviceId;
      const updatedDevice = new Device(deviceId, currentData);

      this.updateDeviceData(updatedDevice);
    }
  }

  private async createDevice(trackInstall = true): Promise<void> {
    const deviceData = this.collectDeviceData();
    this.deviceStorage.data = deviceData;

    const device = new Device(this.deviceStorage.deviceId, deviceData);
    const session = this.sessionService.create(device);
    return this.persistDevice(device, session, trackInstall).then((): void => {
      isPrivateMode()
        .then((isPrivate): void => {
          const updatedSession = this.sessionService.get().withAddedProperties({
            incognito: isPrivate,
          });

          this.sessionService.updateSessionData(updatedSession);
        })
        .catch((err): void => {
          console.warn('Failed to check session mode', err);
        });
    });
  }

  private async persistDevice(device: Device, session: Session, trackInstall: boolean): Promise<void> {
    return this.httpClient
      .execute(new CreateDeviceRequest(device, session, trackInstall))
      .then((): void => {
        this.deviceStorage.isRemotelyPersisted = true;
        this.sessionService.markAsRemotelyPersisted();
      })
      .catch((error): void => {
        console.error('Failed to register Device:', error);
      });
  }

  private hasDeviceData(): boolean {
    return null !== this.deviceStorage.data;
  }

  private collectDeviceData(): DeviceData {
    const navigatorData = new UAParser();
    return new DeviceData(
      FasterConfiguration.FASTER_PLATFORM,
      navigatorData.getOS().name || 'undefined',
      navigatorData.getOS().version || 'undefined',
      navigatorData.getDevice().vendor,
      navigatorData.getDevice().model,
      this.configuration.options.appVersion,
      FasterConfiguration.FASTER_SDK_VERSION,
      null,
      null,
      null,
      null,
      null,
      new Date().toTimeString().slice(9).slice(0, 8),
      navigator.language,
      this.deviceStorage.isNewInstall,
      {
        browserName: navigatorData.getBrowser().name,
        browserVersion: navigatorData.getBrowser().version,
      }
    );
  }
}
